import { useMemo } from "react";

import useWorkLifecycle from "./useWorkLifecycle";
import { createBinatedLifecycleAdapter } from "../services/workLifecycle/adapters";

const getRejectedComment = (proofRow = {}) => {
  const auditTrail = Array.isArray(proofRow?.auditTrail) ? proofRow.auditTrail : [];
  for (let idx = auditTrail.length - 1; idx >= 0; idx -= 1) {
    const event = auditTrail[idx] || {};
    const state = String(event?.state || event?.type || "").trim().toLowerCase();
    if (state !== "rejected") continue;
    const comment = String(event?.comments || event?.comment || "").trim();
    if (comment) return comment;
  }

  const comments = String(proofRow?.comments || "").trim();
  if (!comments) return "";
  const lines = comments
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
  for (let idx = lines.length - 1; idx >= 0; idx -= 1) {
    if (/^client:/i.test(lines[idx])) {
      return lines[idx].replace(/^client:\s*/i, "").trim();
    }
  }
  return comments;
};

const buildRevisionContextFromMappingResponse = (response = {}, file = null) => {
  const fileId = String(file?.fileId || "").trim();
  if (!fileId) return null;

  const mappings = Array.isArray(response?.mappings) ? response.mappings : [];
  const artworks = Array.isArray(response?.artworks) ? response.artworks : [];
  const candidates = mappings
    .filter((mapping) => String(mapping?.sourceFileId || "") === fileId && mapping?.isActive !== false)
    .sort((left, right) => {
      const leftRejected =
        String(left?.proofRow?.status || left?.proofRow?.proofStatus || "").toLowerCase() ===
        "rejected"
          ? 1
          : 0;
      const rightRejected =
        String(right?.proofRow?.status || right?.proofRow?.proofStatus || "").toLowerCase() ===
        "rejected"
          ? 1
          : 0;
      if (leftRejected !== rightRejected) return rightRejected - leftRejected;
      return new Date(right?.updatedAt || 0).getTime() - new Date(left?.updatedAt || 0).getTime();
    });

  const mapping = candidates[0] || null;
  if (!mapping) return null;

  const artwork =
    artworks.find((entry) => Number(entry?.mappedProof?.mappingId) === Number(mapping?.id)) || null;
  const customerRejectedComment = getRejectedComment(mapping?.proofRow || {});
  const defaultNote = customerRejectedComment
    ? `Customer requested changes:\n${customerRejectedComment}`
    : "";

  return {
    mappingId: mapping?.id ?? null,
    proofId: mapping?.proofRow?.id || null,
    sourceFileId: mapping?.sourceFileId || fileId,
    sourceFileName: mapping?.sourceFileName || file?.fileName || null,
    sourceFilePath: mapping?.sourceFilePath || file?.downloadUrl || file?.previewUrl || null,
    documentType: response?.documentType || mapping?.documentType || null,
    documentId: response?.documentId || mapping?.documentId || null,
    logoName: artwork?.logoName || null,
    imprintType: artwork?.imprintType || null,
    location: artwork?.location || null,
    originalImage: artwork?.originalImage || null,
    customerRejectedComment,
    defaultNote,
    isRejected:
      String(mapping?.proofRow?.status || mapping?.proofRow?.proofStatus || "").toLowerCase() ===
      "rejected",
  };
};

export default function useBinatedLifecycle({
  enabled = true,
  isOpen = false,
  isActive = false,
  orderId,
  documentType,
  documentId,
  requestType = "art",
  adapter = null,
  onError,
}) {
  const lifecycleAdapter = useMemo(
    () => adapter || createBinatedLifecycleAdapter(),
    [adapter]
  );
  const contextPayload = useMemo(
    () => ({
      documentType,
      documentId: String(documentId || ""),
      requestType,
    }),
    [documentId, documentType, requestType]
  );

  return useWorkLifecycle({
    enabled,
    isOpen,
    isActive,
    resourceId: orderId,
    contextPayload,
    adapter: lifecycleAdapter,
    buildRevisionContextFromResponse: buildRevisionContextFromMappingResponse,
    onError,
  });
}
